import { Avatar, Box, Group, Stack, Text, ThemeIcon, Title, VisuallyHidden } from '@mantine/core';
import { IconCrown } from '@tabler/icons-react';
import { IllustrativeBadge } from '~portal/components/common';
import type { WorkspaceMember } from '~portal/data';
import { memberDisplayName } from '~portal/data/account';
import * as styles from './home.css';

export const teamSizeLabel = (count: number): string =>
  `${count} ${count === 1 ? 'user' : 'users'} in your team`;

export type WorkspaceSectionProps = {
  /** @desc The heading is the workspace name itself (H10). */
  name: string;
  members: readonly WorkspaceMember[];
};

/**
 * @desc Workspace (H10, Figma `426:7806`): the workspace name as the heading, no enclosing card,
 * rows of initials tile, name and role (never photos — client decision), a crown on the admin row
 * only and "<n> users in your team" on the right. The signed-in user heads the list (`useWorkspace`
 * merges them in); the rest of the team is illustrative and badged so. The admin link is in the
 * page footer only, as Figma places it.
 */
export function WorkspaceSection({ name, members }: WorkspaceSectionProps) {
  return (
    <Stack gap="sm" component="section" aria-labelledby="home-workspace">
      <Group justify="space-between" align="center" gap="sm" wrap="wrap">
        <Group gap="xs" align="center" wrap="wrap">
          <Title order={2} id="home-workspace">
            {name}
          </Title>
          <IllustrativeBadge label="Illustrative team" />
        </Group>
        <Text size="sm" c="dimmed">
          {teamSizeLabel(members.length)}
        </Text>
      </Group>
      <Box component="ul" className={styles.bareRows}>
        {members.map((member) => {
          const displayName = memberDisplayName(member);
          return (
            <li key={member.id} className={styles.workspaceRow}>
              <Avatar name={displayName} color="initials" radius="lg" size={44} />
              <Box miw={0} flex={1}>
                <Text fw={500} truncate>
                  {displayName}
                </Text>
                <Text size="sm" c="dimmed" truncate>
                  {member.jobTitle}
                </Text>
              </Box>
              {member.role === 'admin' && (
                <ThemeIcon variant="transparent" color="yellow" size="md" title="Workspace admin">
                  <IconCrown size={20} aria-hidden />
                  <VisuallyHidden>Admin</VisuallyHidden>
                </ThemeIcon>
              )}
            </li>
          );
        })}
      </Box>
    </Stack>
  );
}
